import React from "react";

import * as AccountAPI from "api/AccountAPI";
import * as AuthAPI from "api/AuthAPI";
import * as DatasetAPI from "api/DatasetAPI";
import * as TagAPI from "api/TagAPI";

import { push, replace } from "connected-react-router";
import { toast } from "react-toastify";

import { updateError as updateGlobalError } from "containers/Errors/reducer";
import * as loginReducer from "containers/Login/reducer";
import * as adminReducer from "containers/Admin/reducer";

import { getSelectedFolder, init as initFolders } from "containers/Folders/reducer";

import { getJobsCount, getJobsByArr, fetchNextRuns, updateJobs } from "containers/Run/reducer";
import { fetchScores, getScoresArr } from "containers/Deploy/reducer";
import { fetchRecipes, getRecipes } from "containers/Recipe/reducer";
import { fetchNextBlueprints, getSortedBlueprints } from "containers/Blueprint/reducer";
import { fetchNextDatasets, getDatasetsArr, fetchPinnedDatasets } from "containers/Home/reducer";
import { fetchAllDeployments, getDeploymentsArr } from "containers/Deployments/reducer";
import { fetchAll as fetchModelGroups } from "containers/ModelGroup/reducer";
import { initFromStorage as fetchPredict } from "containers/Predict/reducer";
import { initOptimizerStorage } from "containers/Optimizer/reducer";
import { fetchSchemaMonitors } from "containers/SchemaMonitor/reducer";
import * as modelOptionsReducer from "containers/CurrentState/reducer";
import * as globalReducer from "containers/App/reducer";
import * as projectReducer from "containers/Project/reducer";
import * as mobileReducer from "containers/MonitoringMobile/reducer";
import * as paginationReducer from "containers/Pagination/reducer";
import * as reportReducer from "containers/Report/ReportReducer";
import * as nextReducer from "containers/Next/NextReducer";
import * as viewReducer from "containers/View/reducer";
import * as lookerSettingsReducer from "containers/LookerSettings/reducer";
import * as tasksReducer from "containers/Tasks/TasksReducer";
import * as homeReducer from "containers/Home/reducer";
import * as foldersReducer from "containers/Folders/reducer";
import * as artifactsReducer from "containers/LanguageModelArtifact/reducer";
import * as aiServiceReducer from "containers/AiService/reducer";
import * as chatGPTReducer from "containers/ChatGPT/reducer";
import * as modelGardenReducer from "containers/ModelGarden/reducer";
import * as analyticsReducer from "containers/Analytics/reducer";
import * as fineTuningReducer from "containers/FineTuning/reducer";
import * as contentGenerationReducer from "containers/ContentGeneration/reducer";

import { init as consultingInit } from "containers/ConsultingProjects/reducer";
import { init as marketplaceInit } from "containers/Marketplace/reducer";
import { handleStateReset } from "containers/Login/reducer";

import { isEnabled } from "utils/featureFlags";

import { getAccountIdFromLastLogin } from "utils/request";

import AWSAccountSettings from "images/AWS-accountsettings.png";

import awsIcon from "images/logo-aws.svg";
import azureIcon from "images/logo-azure.svg";
import bigqueryIcon from "images/bigquery.svg";
import gcpIcon from "images/logo-gcp.svg";
import { fetchAllTones } from "containers/ChatTone/reducer";

import { AibleClientFactory } from "client/aible/factory";
import { OPENSHELL_BASE_URL } from "@/components/Openshell/OpenShellConfig";

export const ACCOUNT_LOGOS = {
  AWS: awsIcon,
  AZURE: azureIcon,
  BIGQUERY: bigqueryIcon,
  GCP: gcpIcon
};

const UPDATE_FIELD = "accounts/UPDATE_FIELD";
const UPDATE_ACCOUNT = "accounts/UPDATE_ACCOUNT";
const UPDATE_ACCOUNTS = "accounts/UPDATE_ACCOUNTS";
const DELETE_ACCOUNT = "accounts/DELETE_ACCOUNT";
const INIT_ACCOUNTS = "accounts/INIT_ACCOUNTS";

const initialState = {
  selectedAccount: getAccountIdFromLastLogin(),
  isCreating: false,
  isLoading: false,
  accounts: {},
  isDefaultAccount: false
};

function accountsReducer(state = initialState, action) {
  switch (action.type) {
    case UPDATE_FIELD:
      return {
        ...state,
        [action.key]: action.data
      };
    case UPDATE_ACCOUNT:
      return {
        ...state,
        ["accounts"]: {
          ...state.accounts,
          [action.key]: action.data
        }
      };
    case DELETE_ACCOUNT:
      return {
        ...state,
        ["accounts"]: Object.assign(
          {},
          ...Object.keys(state.accounts)
            .filter((k) => k + "" !== action.key + "")
            .map((k) => ({ [k]: state.accounts[k] }))
        )
      };
    case UPDATE_ACCOUNTS:
      return {
        ...state,
        ["accounts"]: {
          ...action.data
        }
      };
    case INIT_ACCOUNTS:
      return {
        ...state,
        ...action.data
      };
    default:
      return state;
  }
}

const arrayToObject = (arr, keyField) => Object.assign({}, ...arr.map((item) => ({ [item[keyField]]: item })));

/* Selectors */
export function getIsAzureAccount() {
  return isEnabled("azure-account");
}

export function getBaseAccounts(state) {
  return state.accounts;
}

export function getAccountsRaw(state) {
  return getBaseAccounts(state).accounts;
}

export function getAccount(state, id) {
  return getAccountsRaw(state)[id];
}

// account executor is deprecated
export function getAccountExecutor(state, id) {
  const account = getAccount(state, id);
  return account && account.configuration && account.configuration.executor;
}

export function getAccountAPIEndpoint(state, id) {
  const account = getAccount(state, id);
  let endpoint;
  if (account?.type === "AWS") {
    endpoint = account?.configuration?.lambda_api_endpoint;
  } else {
    endpoint = account?.configuration?.cloud_base_url;
  }

  if (!endpoint) {
    endpoint =
      account?.configuration?.executor?.endpoint_url ??
      account?.configuration?.cloud_base_url ??
      account?.configuration?.lambda_api_endpoint;
  }

  return endpoint;
}

export function getAccounts(state) {
  const accounts = getAccountsRaw(state);
  return Object.keys(accounts)
    .map((aKey) => accounts[aKey])
    .reverse();
}

export function getActiveAccounts(state) {
  return getAccounts(state).filter((a) => a.status === "ACTIVE");
}

export function getProcessingAccounts(state) {
  return getAccounts(state).filter((a) => a.status !== "ACTIVE" && a.status !== "ERROR");
}

export function getSelectedAccountId(state) {
  return getBaseAccounts(state).selectedAccount;
}

export function getSelectedAccount(state) {
  const selectedAccountId = getSelectedAccountId(state);

  return getAccount(state, selectedAccountId);
}

export function getSelectedAccountType(state) {
  const selectedAccount = getSelectedAccount(state);
  return selectedAccount ? selectedAccount.type : "";
}

export function getAccountType(state, id) {
  return (getAccount(state, id) || {}).type;
}

export function getAccountName(state, id) {
  return (getAccount(state, id) || {}).name;
}

export function getIsDefaultAccount(state) {
  const defaultAccountName = "Aible Sample Account";
  const selectedAccount = getSelectedAccount(state);
  return selectedAccount && selectedAccount.name === defaultAccountName;
}

export function getIsCreating(state) {
  return getBaseAccounts(state).isCreating;
}

export function getIsLoading(state) {
  return getBaseAccounts(state).isLoading;
}

export function getAvailableAccountTypes(state) {
  return [...new Set(getAccounts(state).map((a) => a.type))];
}

/* Actions */
export function initAccounts(accountsState) {
  return { type: INIT_ACCOUNTS, data: accountsState };
}

export function updateSelectedAccount(selectedAccount) {
  return (dispatch, getState) => {
    dispatch(loginReducer.storeAccountId(selectedAccount));
    dispatch(loginReducer.storeLatestAccountId(selectedAccount));

    return dispatch({
      type: UPDATE_FIELD,
      data: selectedAccount,
      key: "selectedAccount"
    });
  };
}

export function updateSelectedAccountToFirstOfTypes(types) {
  return (dispatch, getState) => {
    const account = getAccounts(getState()).find((a) => types.includes(a.type));

    if (account) {
      dispatch(updateSelectedAccount(account.id));
    }
  };
}

export function updateAccount(account) {
  return { type: UPDATE_ACCOUNT, data: account, key: account.id };
}

export function deleteAccount(accountId) {
  return { type: DELETE_ACCOUNT, key: accountId };
}

export function updateAccounts(accounts) {
  return { type: UPDATE_ACCOUNTS, data: accounts };
}

export function updateIsCreating(isCreating) {
  return { type: UPDATE_FIELD, data: isCreating, key: "isCreating" };
}

export function updateIsLoading(isLoading) {
  return { type: UPDATE_FIELD, data: isLoading, key: "isLoading" };
}

export function handleDelete(accountId) {
  return (dispatch, getState) => {
    const shouldDelete = confirm("Are you sure you want to delete this account?");

    if (shouldDelete) {
      return AccountAPI.handleDelete(accountId).then((response) => {
        if (response && response.deleted && response.deleted === "DELETED") {
          return dispatch(deleteAccount(response.id));
        }
      });
    } else {
      return Promise.resolve();
    }
  };
}

export function handleSelect(accountId) {
  return (dispatch, getState) => {
    // dispatch(handleStateReset());
    return dispatch(updateSelectedAccount(accountId));
    // return dispatch(fetchAll());
  };
}

export function updateLookerURL(data) {
  return (dispatch, getState) => {
    if (!isEnabled("tenant-edit-looker-url")) {
      return;
    }

    const id = data.id;
    const lookerURL = data.lookerURL;

    AccountAPI.updateLookerURL(id, lookerURL).then((r) => {
      dispatch(updateAccount(r));
    });
  };
}

export function pollAccounts() {
  return (dispatch, getState) => {
    const state = getState();

    if (loginReducer.getAuthenticated(state) === true) {
      const accounts = getProcessingAccounts(state);

      for (const account of accounts) {
        dispatch(fetchAccount(account.id));
      }
    }
  };
}

export function testAccountLimits(id) {
  return (dispatch, getState) => {
    const shouldTest = confirm(
      "Testing AWS account limits will prevent you " +
        "from uploading any dataset and running jobs. This " +
        "process can take up to an hour. Would you like to proceed?"
    );

    if (shouldTest) {
      return AccountAPI.testAccountLimits(id).then((account) => {
        setTimeout(() => dispatch(fetchAccount(id)), 10000);
        return dispatch(updateAccount(account));
      });
    } else {
      return Promise.resolve();
    }
  };
}

export function fetchAccount(id) {
  return (dispatch, getState) => {
    return AccountAPI.getAccount(id).then((account) => {
      dispatch(updateAccount(account));
      return dispatch(fetchContainerTag(account));
    });
  };
}

export function addNewAccount(account) {
  return (dispatch, getState) => {
    if (account && !isNaN(account.id)) {
      dispatch(handleSelect(account.id));
    }
    return dispatch(updateAccount(account));
  };
}

export function handleCreateAWSAccount(name, awsAccountId) {
  return (dispatch, getState) => {
    dispatch(updateIsCreating(true));
    return AccountAPI.createAccount(name, awsAccountId)
      .then((account) => {
        dispatch(updateIsCreating(false));
        return dispatch(addNewAccount(account));
      })
      .catch((error) => {
        toast.error(
          "The data provided did not match to any known AWS account. Please check that the data fields you supplied were correct and try again."
        );
        return dispatch(updateIsCreating(false));
      });
  };
}

export function handleCreateAzureAccount(name, config) {
  return (dispatch, getState) => {
    dispatch(updateIsCreating(true));
    return AccountAPI.createAzureAccount(name, config)
      .then((account) => {
        //seems like this returns even if there is no azure account
        dispatch(updateIsCreating(false));
        return dispatch(addNewAccount(account));
      })
      .catch((error) => {
        toast.error(
          "The data provided did not match to any known Azure account. Please check that the data fields you supplied were correct and try again."
        );
        return dispatch(updateIsCreating(false));
      });
  };
}

export function handleCreateGCPAccount(name, config) {
  return (dispatch, getState) => {
    dispatch(updateIsCreating(true));
    return AccountAPI.createGCPAccount(name, config)
      .then((account) => {
        dispatch(updateIsCreating(false));
        return dispatch(addNewAccount(account));
      })
      .catch((error) => {
        toast.error(
          "The data provided did not match to any known GCP account. Please check that the data fields you supplied were correct and try again."
        );
        return dispatch(updateIsCreating(false));
      });
  };
}

export function fetchAll(overrideLimit = false, isInit = false) {
  return (dispatch, getState) => {
    // Fetches data from the backend to populate the currently selected account

    const state = getState();
    const selectedFolderId = foldersReducer.getSelectedFolder(state);

    const promises = [];

    dispatch(paginationReducer.resetPagination());

    // (getJobsByArr(state).length < 2 || overrideLimit) && dispatch(fetchNextRuns(false, true));
    if (getSortedBlueprints(state).length < 20 || overrideLimit) {
      dispatch(fetchNextBlueprints(isInit));
    }
    // Temp hack to delay race condition
    if (getDatasetsArr(state).length < 2 || overrideLimit) {
      promises.push(dispatch(fetchNextDatasets(false, true, undefined, isEnabled("aible-consumer"))));
    }

    dispatch(fetchPinnedDatasets());

    /* Removing as of 4/9/21
		(getScoresArr(state).length < 2 || overrideLimit) && dispatch(fetchScores());
		(getRecipes(state).length < 2 || overrideLimit) &&
			dispatch(globalReducer.updateIsRecipesLoading(true)) &&
			dispatch(fetchRecipes());
		dispatch(consultingInit());
		dispatch(marketplaceInit());
		*/

    dispatch(fetchPredict());

    dispatch(fetchSchemaMonitors());

    dispatch(initOptimizerStorage());

    // TODO: This is for LOCAL reports, add fetch here for real reports
    // Moving this to happen when user views all reports
    dispatch(reportReducer.initFromStorage());
    // TODO: This is a hack to make the fake market tiles always show some hex
    //dispatch(nextReducer.init(17, 6, true));

    // dispatch(projectReducer.fetchProjects()).then((response) => {
    // 	dispatch(fetchAllDeployments());
    // });

    // dispatch(projectReducer.fetchProjects(isEnabled('aible-consumer'))).then(
    // 	() => {
    // 		(getDeploymentsArr(state).length < 2 || overrideLimit) &&
    // 			dispatch(globalReducer.updateIsDeploymentsLoading(true)) &&
    // 			dispatch(fetchAllDeployments());

    // 		Object.keys(projectReducer.getBase(getState())).map(
    // 			projectId => dispatch(projectReducer.init(projectId))
    // 		)
    // 	}
    // );

    dispatch(globalReducer.updateIsDeploymentsLoading(false));
    //dispatch(globalReducer.updateIsOptimizersLoading(false));

    dispatch(adminReducer.updateUsersCall(loginReducer.getSelectedTenant(state)));

    dispatch(tasksReducer.fetchTasks());

    const coreSDK = loginReducer.getLookerAPIClient(getState());
    if (coreSDK !== undefined) {
      dispatch(lookerNavigateToDashboard());
    }

    AuthAPI.getAppConfig().then(({ env }) => dispatch(globalReducer.updateEnvironment(env)));

    // const accountId = getSelectedAccountId(getState());
    // const folderId = foldersReducer.getSelectedFolder(getState());

    dispatch(artifactsReducer.fetchAllArtifacts());
    // We are using AiService to handle models and connections that LanguageModelArtifacts used to
    dispatch(
      aiServiceReducer.fetchAll({
        account_id: getSelectedAccountId(state)
      })
    );

    // Fetching executions and example sets
    // executions are currently folder agnostic
    // example sets (training datasets) are folder specific
    dispatch(fineTuningReducer.fetchFineTuningExecutions({}));
    dispatch(contentGenerationReducer.fetchExampleSets({ folder_id: selectedFolderId }));

    dispatch(fetchAllTones());

    // dispatch(fetchAllContainerTags());

    dispatch(modelOptionsReducer.fetchModelOptions());

    return Promise.all(promises).catch((e) => {
      console.log(e);
    });
  };
}

export function lookerNavigateToDashboard() {
  return async (dispatch, getState) => {
    const coreSDK = loginReducer.getLookerAPIClient(getState());
    const extensionSDK = loginReducer.getExtensionSDK(getState());
    const urlPath = extensionSDK._lookerHostData.route;

    const lookerUser = await coreSDK.me();

    coreSDK.user_attribute_user_values({ user_id: lookerUser?.value?.id }).then(async (r) => {
      dispatch(lookerSettingsReducer.handleFetchAttributes(r));

      const params = urlPath === undefined ? undefined : urlPath.split("/");

      console.log("what are my params yo? params, urlPath alll atributes", params, urlPath, r);

      if (params?.[1] === "ChatAible") {
        const nextId = params?.[2];
        const messageId = params?.[3];
        const accountId = params?.[4];
        console.log("YO YO CHATAIBLE! nextId messageId", nextId, messageId, accountId);

        if (accountId !== undefined) {
          dispatch(updateSelectedAccount(accountId));
        }
        return dispatch(nextReducer.openChatAible(nextId)).then((r) => {
          const datasetId = nextReducer.getDatasetId(getState(), nextId);

          console.log("we got dartaset id yert?", datasetId);

          dispatch(homeReducer.fetchDataset(datasetId));
          return dispatch(waiterDataset(nextId, messageId));
        });
      } else {
        const recentDatasetId = params?.[1];
        const recentSuccessValue = params?.[2];
        const accountId = params?.[3];

        if (accountId !== undefined) {
          dispatch(updateSelectedAccount(accountId));
        }

        if (recentDatasetId === undefined || lookerSettingsReducer.getHasAllAttributes(getState()) === false) {
          dispatch(push("/lookerSetup"));
          dispatch(lookerSettingsReducer.updateLookerUserAttributes(r));
          dispatch(lookerSettingsReducer.updateIsOpen(true));
        } else {
          dispatch(
            nextReducer.openExploration(recentDatasetId, {
              value: {
                value: recentSuccessValue,
                label: recentSuccessValue
              }
            })
          );
        }
      }
    });
  };
}

export function waiterDataset(nextId, messageId) {
  return (dispatch, getState) => {
    const datasetId = nextReducer.getDatasetId(getState(), nextId);
    const dataset = homeReducer.getDataset(getState(), datasetId);

    console.log("waiterDataset datasetId nextId messageId", datasetId, nextId, messageId);

    if (dataset === undefined) {
      return setTimeout(() => dispatch(waiterDataset(nextId, messageId)), 1000);
    } else {
      return dispatch(homeReducer.checkUpdateLooker(datasetId, nextId, messageId));
    }
  };
}

export function init(noLogin, override = false, uiState) {
  return (dispatch, getState) => {
    const selectedTenant = loginReducer.getSelectedTenant(getState());
    const currAccounts = getAccounts(getState());
    if (currAccounts && currAccounts.length && !override) {
      return Promise.resolve();
    }

    dispatch(updateIsLoading(true));
    dispatch(updateAccounts({}));

    return AccountAPI.getAccounts().then((accounts) => {
      dispatch(updateAccounts(arrayToObject(accounts, "id")));

      dispatch(updateIsLoading(false));

      dispatch(loginReducer.updateIsAuthenticated(true));

      if (accounts && accounts.length > 0) {
        const latestAccountId = Number(loginReducer.getLatestAccountId(getState()));

        if (latestAccountId && getAccount(getState(), latestAccountId)) {
          dispatch(updateSelectedAccount(latestAccountId));
        } else {
          let latestAccount = undefined;
          if (selectedTenant) {
            latestAccount = accounts
              .filter((a) => a.status === "ACTIVE")
              .filter((a) => a.tenant_id === selectedTenant.id);
            const defaultAccount = getSelectedAccountId(getState());
            let tempAccount = latestAccount;
            if (defaultAccount) {
              tempAccount = latestAccount.filter((a) => a.id === defaultAccount);
              if (tempAccount.length <= 0) {
                tempAccount = latestAccount;
              }
            }

            latestAccount =
              tempAccount.length !== 1
                ? latestAccount.reduce((a, b) => {
                    if (a.type === "GCP" && b.type !== "GCP") {
                      return a;
                    }
                    if (a.type !== "GCP" && b.type === "GCP") {
                      return b;
                    }
                    return a.id > b.id ? a : b;
                  }, [])
                : tempAccount[0];
          }

          const lookerExportAccount = accounts.find((ac) => ac?.configuration?.looker_export_url !== undefined);

          const lookerAPI = loginReducer.getLookerAPIClient(getState());

          if (lookerExportAccount !== undefined && lookerAPI !== undefined) {
            dispatch(updateSelectedAccount(lookerExportAccount?.id));
          } else if (latestAccount && latestAccount.id) {
            dispatch(updateSelectedAccount(latestAccount.id));
          } else {
            dispatch(updateSelectedAccount(accounts[accounts.length - 1]?.id));
          }
        }
        if (noLogin !== true) {
          if (uiState?.type === "SHARE" && uiState?.id) {
            const url = new URL(uiState?.link);
            const path = url?.pathname ?? `/next/new/new/${uiState?.id}`;
            dispatch(push(path));
          } else if (getState().router?.location?.state?.from) {
            dispatch(push(getState().router.location.state.from));
          } else {
            dispatch(push("/"));
          }
        }
      } else {
        toast("No accounts detected. Please create and link a cloud account.", {
          type: toast.TYPE.ERROR,
          position: toast.POSITION.TOP_CENTER,
          autoClose: 5000
        });
        // dispatch(
        // 	updateGlobalError(
        // 		"No accounts detected. Please create and link a cloud account."
        // 	)
        // );
        dispatch(push("/accounts"));
      }

      dispatch(initFolders());
      dispatch(fetchAllContainerTags());
      return Promise.resolve();
    });
  };
}

export function checkAccount() {
  return (dispatch, getState) => {
    const defaultAccountName = "Aible Sample Account";
    const state = getState();
    const selectedAccount = getSelectedAccount(state);

    if (selectedAccount && selectedAccount.name === defaultAccountName) {
      const toastId = "default-account-selected";
      const toastStyle = {
        marginTop: ".5em",
        marginBottom: "1em",
        display: "block",
        marginLeft: "auto",
        marginRight: "auto",
        maxWidth: "90%",
        width: "auto",
        height: "auto",
        maxHeight: "50px"
      };
      toast.info(
        <div onClick={() => dispatch(push("/accounts"))}>
          <p>It looks like you still have the default Aible account selected.</p>
          <p>
            In order to run models on your own data, you will have to link your AWS account in your account settings.
          </p>
          <img style={toastStyle} src={AWSAccountSettings} alt="Aible Account Settings" />
        </div>,
        {
          toastId,
          autoClose: false,
          position: toast.POSITION.TOP_CENTER
        }
      );
    }
  };
}

export function fetchAllContainerTags() {
  return (dispatch, getState) => {
    const accounts = getAccounts(getState()) ?? [];

    accounts.forEach(async (account) => {
      return dispatch(fetchContainerTag(account));
    });
  };
}

export function fetchContainerTag(account) {
  return (dispatch, getState) => {
    if (account?.status !== "ACTIVE") {
      return Promise.resolve();
    }
    const accountClient = AibleClientFactory.getInstance().getAccountClient();
    return accountClient.getAccountClient(account).then((client) =>
      client.get("v1/container/tag").then((response) => {
        const retAccount = {
          ...account,
          container: response
        };
        const persisted = getAccount(getState(), retAccount?.id);
        if (persisted === undefined) {
          throw Error("cannot update missing account");
        }
        return dispatch(updateAccount(retAccount));
      })
    );
  };
}

export function getOpenShellBaseUrl(state) {
  const account = getSelectedAccount(state);

  if (account?.configuration?.openshell_base_url !== undefined) {
    return account.configuration.openshell_base_url;
  }

  return OPENSHELL_BASE_URL;
}

export default accountsReducer;
